import React from 'react';
import { 
  AlertTriangle, 
  Target, 
  BarChart3, 
  Users, 
  MessageSquare, 
  Plus, 
  Search, 
  ChevronRight 
} from 'lucide-react';

const CampaignDashboard = ({ 
  campaigns, 
  searchTerm, 
  setSearchTerm, 
  filterStatus, 
  setFilterStatus, 
  canEditAISettings, 
  canViewAISettings, 
  canEditInstructions,
  setSelectedCampaign, 
  setActiveTab 
}) => {
  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || campaign.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-8">
      {!canEditAISettings && canViewAISettings && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
          <span className="text-yellow-800">
            You have read-only access to AI settings. Admin permissions required to modify AI strategies and instructions.
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Campaigns</p>
              <p className="text-3xl font-bold">{campaigns.length}</p>
              <p className="text-blue-100 text-xs mt-1">↑ 2 new this month</p>
            </div>
            <Target className="w-10 h-10 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Avg. Conversion</p>
              <p className="text-3xl font-bold">13.0%</p>
              <p className="text-green-100 text-xs mt-1">↑ 2.1% vs last month</p>
            </div>
            <BarChart3 className="w-10 h-10 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Active Leads</p>
              <p className="text-3xl font-bold">1,792</p>
              <p className="text-purple-100 text-xs mt-1">Across all campaigns</p>
            </div>
            <Users className="w-10 h-10 text-purple-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Messages Sent</p>
              <p className="text-3xl font-bold">12.8K</p>
              <p className="text-orange-100 text-xs mt-1">This month</p>
            </div>
            <MessageSquare className="w-10 h-10 text-orange-200" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Campaign Portfolio</h2>
            <p className="text-gray-600 mt-1">Manage AI strategies across all your campaigns</p>
          </div>
          {canEditInstructions && (
            <button 
              onClick={() => {
                setSelectedCampaign(null);
                setActiveTab('campaign-builder');
              }}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium"
            >
              <Plus className="w-5 h-5" />
              <span>Create New Strategy</span>
            </button>
          )}
        </div>

        <div className="flex items-center space-x-4 mb-6">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="draft">Draft</option>
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              onClick={() => {
                if (canViewAISettings) {
                  setSelectedCampaign(campaign);
                }
              }}
              className={`bg-white rounded-xl border-2 border-gray-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all group ${
                canViewAISettings ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${
                    (campaign.status || campaign.is_active) === 'active' || campaign.is_active ? 'bg-green-500' : 
                    (campaign.status || campaign.is_active) === 'paused' ? 'bg-yellow-500' : 'bg-gray-400'
                  }`}></div>
                  <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                    (campaign.status || campaign.is_active) === 'active' || campaign.is_active 
                      ? 'bg-green-100 text-green-700' 
                      : (campaign.status || campaign.is_active) === 'paused'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {((campaign.status || (campaign.is_active ? 'active' : 'inactive')) || 'UNKNOWN').toUpperCase()}
                  </span>
                </div>
                {canViewAISettings && (
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                )}
              </div>

              <h3 className="font-bold text-lg text-gray-900 mb-3">{campaign.name}</h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm">Active Leads</span>
                  <span className="font-bold text-gray-900">{(campaign.leads || 0).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm">Conversion Rate</span>
                  <span className="font-bold text-green-600">{campaign.conversion || '0.0'}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 text-sm">AI Persona</span>
                  <span className="font-medium text-gray-900 text-sm">{campaign.aiPersona || 'Not set'}</span>
                </div>
              </div>

              <div className="pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Modified {campaign.lastModified || 'Unknown'}</span>
                  <span className="px-2 py-1 bg-gray-100 rounded-full capitalize text-gray-700">
                    {(campaign.strategy || 'default').replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">AI Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-xl p-4">
            <h4 className="font-medium text-blue-900 mb-2">Top Performing Persona</h4>
            <div className="text-2xl font-bold text-blue-600">Senior Account Executive</div>
            <div className="text-sm text-blue-700">18.7% avg conversion</div>
          </div>
          <div className="bg-green-50 rounded-xl p-4">
            <h4 className="font-medium text-green-900 mb-2">Optimal Follow-up Timing</h4>
            <div className="text-2xl font-bold text-green-600">Day 3</div>
            <div className="text-sm text-green-700">23.4% response rate</div>
          </div>
          <div className="bg-purple-50 rounded-xl p-4">
            <h4 className="font-medium text-purple-900 mb-2">Best Industry Strategy</h4>
            <div className="text-2xl font-bold text-purple-600">Consultative</div>
            <div className="text-sm text-purple-700">For B2B SaaS campaigns</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignDashboard;